import { useParams } from "react-router-dom";
import { useCallback, useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ProjectType } from "@/consts/Project";
import { useReadProjectGroupsItemSheetOriginalList } from "@/libs/react-query/hooks/useItemsheetsQuery";
import { useEstimateInfo, useSetEstimateInfo } from "../hooks/useScenarioStore";

export default function ScenarioEstimateSelect() {
  const { group_id = "" } = useParams();

  const estimateInfo = useEstimateInfo();
  const setEstimateInfo = useSetEstimateInfo();

  const {
    data: originals,
    isLoading: isLoadingOriginals,
    isError: isErrorOriginals,
  } = useReadProjectGroupsItemSheetOriginalList(group_id);

  const allOriginals = useMemo(() => {
    if (!originals) return [];

    const { proj_originals: projArr = [], prcr_originals: prcrArr = [] } =
      originals;

    const mergedArr = [
      ...projArr.map((item) => ({ ...item, project_type: ProjectType.PROJ })),
      ...prcrArr.map((item) => ({ ...item, project_type: ProjectType.PRCR })),
    ];

    return mergedArr.sort((a, b) => {
      if (a.is_outdated !== b.is_outdated) {
        return a.is_outdated ? 1 : -1;
      }

      if (a.project_type !== b.project_type) {
        return a.project_type === ProjectType.PROJ ? -1 : 1;
      }
      return 0;
    });
  }, [originals]);

  const handleSelectChange = useCallback(
    (value: string) => {
      const selectedItem = allOriginals.find(
        (item) => item.project_id === value
      );
      if (!selectedItem) return;

      setEstimateInfo({
        projectId: selectedItem.project_id,
        projectType: selectedItem.project_type,
        originalId: selectedItem.id,
      });
    },
    [originals, setEstimateInfo]
  );

  if (isLoadingOriginals) {
    return (
      <div className="absolute right-0 -top-14 inline-flex items-center gap-3 justify-end">
        <Skeleton className="w-80 h-10 rounded-sm bg-white" />
      </div>
    );
  }

  if (isErrorOriginals) {
    return null;
  }

  return (
    <div className={"inline-flex items-center gap-3 justify-end"}>
      <p className="text-xs text-gray-B font-bold whitespace-nowrap">공고</p>
      <Select
        onValueChange={handleSelectChange}
        defaultValue={estimateInfo?.projectId ?? undefined}
      >
        <SelectTrigger className="min-w-[300px] text-[10px] text-gray-A font-semibold">
          <SelectValue placeholder="공고를 선택하세요" />
        </SelectTrigger>
        <SelectContent className="min-w-[300px]">
          <SelectGroup>
            {allOriginals.map((item) => (
              <SelectItem
                key={item.project_id}
                value={item.project_id}
                className="!block w-full"
              >
                <div className="flex justify-between items-center gap-5">
                  <div className="flex items-center gap-2 flex-1">
                    <span className="text-[10px] text-gray-A font-semibold">
                      {item.project_title}
                    </span>
                    <div className="flex flex-shrink-0 whitespace-nowrap">
                      {getBadgeByProjectType(item.project_type)}
                    </div>
                  </div>
                  {item.is_outdated && (
                    <div className="flex py-0.5 px-1 whitespace-nowrap flex-shrink-0 text-[6px] leading-[8px] bg-[rgba(238,142,148,0.30)] text-pink font-semibold rounded-[2px]">
                      연결되지 않음
                    </div>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  );
}

const getBadgeByProjectType = (type: ProjectType) => {
  const projectTypeMap = {
    [ProjectType.PROJ]: { label: "입찰공고", color: "bg-mediumGreen" },
    [ProjectType.PRCR]: { label: "사전규격", color: "bg-dark-blue" },
    [ProjectType.PLAN]: { label: "발주계획", color: "bg-mandalay" },
  };

  return (
    <div
      className={`flex py-0.5 px-1 rounded-[2px] text-[8px] leading-[10px] text-white font-semibold ${projectTypeMap[type].color}`}
    >
      {projectTypeMap[type].label}
    </div>
  );
};
